import React, { useState, useEffect } from 'react';
import Icon from '../../../../components/AppIcon';
import Button from '../../../../components/ui/Button';

const LocationVerification = ({ punchInLocation, onLocationCapture, currentLocation }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const calculateDistance = (lat1, lon1, lat2, lon2) => {
    // Ensure all values are numbers
    const lat1Num = typeof lat1 === 'number' ? lat1 : parseFloat(lat1);
    const lon1Num = typeof lon1 === 'number' ? lon1 : parseFloat(lon1);
    const lat2Num = typeof lat2 === 'number' ? lat2 : parseFloat(lat2);
    const lon2Num = typeof lon2 === 'number' ? lon2 : parseFloat(lon2);
    
    if (isNaN(lat1Num) || isNaN(lon1Num) || isNaN(lat2Num) || isNaN(lon2Num)) {
      return null;
    }
    
    const R = 6371; // Earth radius in km
    const dLat = (lat2Num - lat1Num) * Math.PI / 180;
    const dLon = (lon2Num - lon1Num) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1Num * Math.PI / 180) * Math.cos(lat2Num * Math.PI / 180) *
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    const distance = R * c;
    return distance;
  };

  const reverseGeocode = async (lat, lng) => {
    try {
      // Ensure lat and lng are numbers
      const latNum = typeof lat === 'number' ? lat : parseFloat(lat);
      const lngNum = typeof lng === 'number' ? lng : parseFloat(lng);
      
      if (isNaN(latNum) || isNaN(lngNum)) {
        return 'Location not available';
      }
      
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latNum}&lon=${lngNum}`,
        {
          headers: {
            'User-Agent': 'LaborPro-ERP/1.0'
          }
        }
      );
      const data = await response?.json();
      return data?.display_name || `${latNum.toFixed(6)}, ${lngNum.toFixed(6)}`;
    } catch (err) {
      console.error('Reverse geocoding error:', err);
      const latNum = typeof lat === 'number' ? lat : parseFloat(lat);
      const lngNum = typeof lng === 'number' ? lng : parseFloat(lng);
      return isNaN(latNum) || isNaN(lngNum) ? 'Location not available' : `${latNum.toFixed(6)}, ${lngNum.toFixed(6)}`;
    }
  };

  const getCurrentLocation = () => {
    setIsLoading(true);
    setError(null);

    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser.');
      setIsLoading(false);
      return;
    }

    navigator.geolocation?.getCurrentPosition(
      async (position) => {
        const { latitude, longitude, accuracy } = position?.coords;
        
        const address = await reverseGeocode(latitude, longitude);

        onLocationCapture({
          latitude,
          longitude,
          address,
          accuracy: Math.round(accuracy)
        });
        setIsLoading(false);
      },
      (err) => {
        setIsLoading(false);
        if (err?.code === 1) {
          setError('Location access denied. Please enable location permissions in your browser settings.');
        } else if (err?.code === 2) {
          setError('Unable to determine your location. Please check your device settings.');
        } else {
          setError('Location request timed out. Please try again.');
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  useEffect(() => {
    if (!currentLocation) {
      getCurrentLocation();
    }
  }, []);

  const distance = currentLocation && punchInLocation && punchInLocation.latitude && punchInLocation.longitude
    ? calculateDistance(
        punchInLocation.latitude,
        punchInLocation.longitude,
        currentLocation.latitude,
        currentLocation.longitude
      )
    : null;

  const getDistanceStatus = () => {
    if (!distance) return null;
    if (distance < 0.1) return { text: 'Same Location', color: 'text-success', bg: 'bg-success/10', icon: 'CheckCircle2' };
    if (distance < 1) return { text: `${(distance * 1000).toFixed(0)}m away`, color: 'text-warning', bg: 'bg-warning/10', icon: 'AlertCircle' };
    return { text: `${distance.toFixed(2)}km away`, color: 'text-error', bg: 'bg-error/10', icon: 'AlertTriangle' };
  };

  const distanceStatus = getDistanceStatus();

  return (
    <div className="bg-card rounded-xl shadow-elevation-2 p-4 md:p-6 border border-border">
      <div className="flex items-center gap-3 mb-4 md:mb-6">
        <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-primary/10 flex items-center justify-center">
          <Icon name="MapPin" size={20} className="md:w-6 md:h-6 text-primary" />
        </div>
        <div>
          <h2 className="text-base md:text-lg font-semibold text-foreground">Location Verification</h2>
          <p className="text-xs md:text-sm text-muted-foreground">Confirm your punch out location</p>
        </div>
      </div>
      {error ? (
        <div className="flex flex-col items-center justify-center py-6 md:py-8">
          <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-error/10 flex items-center justify-center mb-4">
            <Icon name="MapPinOff" size={32} className="md:w-10 md:h-10 text-error" />
          </div>
          <p className="text-sm md:text-base text-error text-center mb-4 px-4">{error}</p>
          <Button variant="outline" onClick={getCurrentLocation} iconName="RefreshCw" iconPosition="left">
            Retry Location Access
          </Button>
        </div>
      ) : isLoading ? (
        <div className="flex flex-col items-center justify-center py-8 md:py-12">
          <div className="w-12 h-12 md:w-16 md:h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4" />
          <p className="text-sm md:text-base text-foreground">Getting your location...</p>
        </div>
      ) : currentLocation ? (
        <div className="space-y-3 md:space-y-4">
          <div className="p-3 md:p-4 bg-muted/50 rounded-lg">
            <p className="text-xs md:text-sm text-muted-foreground mb-2">Current Location</p>
            <p className="text-sm md:text-base font-medium text-foreground break-words mb-2">{currentLocation.address}</p>
            <p className="text-xs text-muted-foreground">
              {typeof currentLocation.latitude === 'number' ? currentLocation.latitude.toFixed(6) : parseFloat(currentLocation.latitude).toFixed(6)}, {typeof currentLocation.longitude === 'number' ? currentLocation.longitude.toFixed(6) : parseFloat(currentLocation.longitude).toFixed(6)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Accuracy: ±{currentLocation.accuracy}m
            </p>
          </div>

          {distanceStatus && (
            <div className={`flex items-center gap-3 p-3 md:p-4 ${distanceStatus.bg} rounded-lg`}>
              <Icon name={distanceStatus.icon} size={20} className={`${distanceStatus.color} md:w-6 md:h-6`} />
              <div className="flex-1 min-w-0">
                <p className="text-xs md:text-sm text-muted-foreground">Distance from Punch In</p>
                <p className={`text-sm md:text-base font-semibold ${distanceStatus.color}`}>{distanceStatus.text}</p>
              </div>
            </div>
          )}

          <div className="relative w-full rounded-lg overflow-hidden border border-border" style={{ height: '200px' }}>
            <iframe
              width="100%"
              height="100%"
              loading="lazy"
              title="Current punch out location"
              referrerPolicy="no-referrer-when-downgrade"
              src={`https://www.google.com/maps?q=${currentLocation.latitude},${currentLocation.longitude}&z=15&output=embed`}
              className="w-full h-full"
            />
          </div>

          <Button variant="outline" onClick={getCurrentLocation} iconName="RefreshCw" iconPosition="left" fullWidth>
            Refresh Location
          </Button>
        </div>
      ) : null}
    </div>
  );
};

export default LocationVerification;

